import { useMemo, useState } from 'react';
import { ErrorState, LoadingState } from '../components/ScreenState';
import { SpeakButton } from '../components/SpeakButton';
import { ttsTextOf } from '../components/speech';
import { useAsync } from '../hooks/useAsync';
import { fetchLessonItemsForMode } from '../screens/library/lessonItems';
import type { LearningItemView } from '../api/content.types';
import type { FlashcardMode } from '../screens/library/types';

/** Chữ Nhật + cách đọc của 1 item, tuỳ `itemType`. */
function japaneseOf(item: LearningItemView): { main: string; reading: string | null } {
  if (item.itemType === 'VOCAB') return { main: item.kanji ?? item.kana, reading: item.kanji ? item.kana : null };
  if (item.itemType === 'GRAMMAR') return { main: item.title, reading: null };
  // KANJI không có romaji (dùng on/kun-yomi); KANA/RADICAL vẫn dùng romaji.
  const reading = item.itemType === 'KANJI' ? item.kunYomi ?? item.onYomi ?? null : item.romaji;
  return { main: item.character, reading };
}

function meaningOf(item: LearningItemView): string {
  if (item.itemType === 'GRAMMAR') return item.meaningVi ?? item.bodyMarkdown;
  return item.meaningVi ?? '';
}

interface Props {
  unitId: string;
  mode: FlashcardMode;
  onStudy: () => void;
}

/**
 * Danh sách từ vựng/kanji/ngữ pháp của 1 Bài — bước TRƯỚC flashcard: chọn Bài ở thư viện thì
 * xem danh sách trước, bấm "Học bằng flashcard" mới vào bộ thẻ (cùng bộ item, cùng mode).
 */
export function UnitItemListPage({ unitId, mode, onStudy }: Props) {
  const list = useAsync<LearningItemView[]>(() => fetchLessonItemsForMode(unitId, mode), [unitId, mode]);

  const [hideMeaning, setHideMeaning] = useState(false);
  /// Các item người dùng đã tự bấm để lộ nghĩa khi đang bật chế độ tự kiểm tra.
  const [revealedIds, setRevealedIds] = useState<Record<string, true>>({});

  const summary = useMemo(() => {
    const items = list.data ?? [];
    const vocab = items.filter((it) => it.itemType === 'VOCAB').length;
    const grammar = items.filter((it) => it.itemType === 'GRAMMAR').length;
    const kanji = items.filter((it) => it.itemType === 'KANJI').length;
    return [vocab > 0 && `${vocab} từ`, kanji > 0 && `${kanji} kanji`, grammar > 0 && `${grammar} ngữ pháp`].filter(Boolean).join(' · ');
  }, [list.data]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: 'var(--font-vi)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{summary}</div>
        <button
          onClick={() => {
            setRevealedIds({});
            setHideMeaning((h) => !h);
          }}
          aria-pressed={hideMeaning}
          style={{
            border: `1.5px solid ${hideMeaning ? 'var(--cta-default)' : 'var(--border-default)'}`,
            background: hideMeaning ? 'var(--surface-tint)' : 'var(--surface-card)',
            borderRadius: 999,
            padding: '10px 16px',
            cursor: 'pointer',
            fontFamily: 'var(--font-vi)',
            fontSize: 13.5,
            fontWeight: 700,
            color: 'var(--cta-default)',
          }}
        >
          {hideMeaning ? 'Hiện lại nghĩa' : 'Ẩn nghĩa để tự kiểm tra'}
        </button>
      </div>

      {list.loading ? (
        <LoadingState />
      ) : list.error || !list.data ? (
        <ErrorState message="Không tải được danh sách." />
      ) : list.data.length === 0 ? (
        <ErrorState message="Bài này chưa có dữ liệu cho bộ thẻ này." />
      ) : (
        <>
          <div style={{ background: 'var(--surface-page)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
            {list.data.map((item, index) => {
              const jp = japaneseOf(item);
              const meaning = meaningOf(item);
              const meaningHidden = hideMeaning && !revealedIds[item.id];
              return (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 20px',
                    borderBottom: index === list.data!.length - 1 ? 'none' : '1px solid var(--border-default)',
                  }}
                >
                  <span style={{ width: 22, fontSize: 12, fontWeight: 700, color: 'var(--navy-300)', flexShrink: 0 }}>{index + 1}</span>
                  <span style={{ flex: 1.1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontFamily: 'var(--font-jp)', fontSize: 19, fontWeight: 500, color: 'var(--ink)' }}>{jp.main}</span>
                    {jp.reading && <span style={{ display: 'block', fontFamily: 'var(--font-jp)', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{jp.reading}</span>}
                  </span>
                  <button
                    disabled={!meaningHidden}
                    onClick={() => setRevealedIds((prev) => ({ ...prev, [item.id]: true }))}
                    aria-label={meaningHidden ? 'Hiện nghĩa' : undefined}
                    style={{
                      flex: 1.2,
                      minWidth: 0,
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: meaningHidden ? 'pointer' : 'default',
                      fontFamily: 'var(--font-vi)',
                      fontSize: 14,
                      fontWeight: 600,
                      color: meaningHidden ? 'var(--navy-300)' : 'var(--ink)',
                      lineHeight: 1.4,
                    }}
                  >
                    {meaningHidden ? '• • •' : meaning}
                  </button>
                  <SpeakButton text={ttsTextOf(item)} variant="outline" size={38} label={`Nghe phát âm ${jp.main}`} />
                </div>
              );
            })}
          </div>

          <button
            onClick={onStudy}
            style={{
              alignSelf: 'flex-start',
              height: 52,
              padding: '0 28px',
              borderRadius: 'var(--radius-control)',
              background: 'var(--cta-default)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-vi)',
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            Học bằng flashcard
          </button>
        </>
      )}
    </div>
  );
}
